import { Link, useNavigate } from "react-router-dom";
import { ShoppingBag, ArrowRight, Minus, Plus, Trash2, Truck, ShieldCheck, RotateCcw, Tag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCart } from "@/contexts/CartContext";
import { SEOHead } from "@/components/seo/SEOHead";
import { ProductImagePlaceholder } from "@/components/ProductImagePlaceholder";
import { calculateCartBundleDiscount, isBundleEligible } from "@/utils/bundlePricing";
import { calculateShipping, SHIPPING_THRESHOLD } from "@/utils/shippingCalculator";

// Helper to get bundle helper text based on eligible quantity
function getBundleHelperText(eligibleQty: number): string | null {
  if (eligibleQty === 0) return null;
  if (eligibleQty === 1) return "Add 1 more to get 2 for £10";
  if (eligibleQty === 2) return "Add 1 more to get 3 for £12";
  return "Multi-buy discount applied ✅";
}

export default function Cart() {
  const { items, removeItem, updateQuantity, totalPrice, clearCart } = useCart();
  const navigate = useNavigate();

  // Calculate bundle discount
  const bundleResult = calculateCartBundleDiscount(items);
  const subtotalAfterBundle = totalPrice - bundleResult.totalDiscount;
  
  // Calculate shipping
  const shippingResult = calculateShipping(subtotalAfterBundle);
  const orderTotal = subtotalAfterBundle + shippingResult.shippingCost;

  const handleCheckout = () => {
    if (items.length === 0) return;
    navigate('/checkout');
  };

  return (
    <>
      <SEOHead
        title="Your Shopping Cart | Samboards"
        description="Review your shopping cart and proceed to checkout."
        canonical="/cart"
      />
      
      <div className="min-h-[calc(100vh-200px)] bg-gradient-to-b from-[#020817] via-[#04151F] to-[#020817] text-slate-50 py-8 sm:py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl mb-6 sm:mb-8 bg-gradient-to-r from-emerald-200 to-teal-200 bg-clip-text text-transparent">
            Your Cart
          </h1>

          {items.length === 0 ? (
            <div className="text-center py-12 sm:py-16">
              <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 sm:mb-6 rounded-full bg-slate-800/50 border border-slate-700/50 flex items-center justify-center">
                <ShoppingBag className="w-8 h-8 sm:w-10 sm:h-10 text-slate-500" />
              </div>
              <h2 className="text-xl sm:text-2xl font-semibold text-slate-200 mb-2 sm:mb-3">
                Your cart is empty
              </h2>
              <p className="text-sm sm:text-base text-slate-400 mb-6 sm:mb-8 max-w-md mx-auto px-4">
                Looks like you haven't added any products yet. Explore our range of premium sensory tools.
              </p>
              
              {/* Trust indicators */}
              <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-xs text-slate-400 mb-6 sm:mb-8">
                <span>✓ Free delivery over £{SHIPPING_THRESHOLD}</span>
                <span>✓ Secure checkout</span>
                <span>✓ UK designed & made</span>
              </div>
              
              <Button
                asChild
                size="lg"
                className="bg-gradient-to-r from-emerald-400 via-emerald-500 to-teal-500 text-slate-950 font-semibold shadow-[0_18px_55px_rgba(16,185,129,0.45)] hover:shadow-[0_0_55px_rgba(16,185,129,0.6)] text-sm sm:text-base px-6 sm:px-8"
              >
                <Link to="/shop">
                  Browse products
                  <ArrowRight className="ml-2 h-4 w-4 sm:h-5 sm:w-5" />
                </Link>
              </Button>
            </div>
          ) : (
            <div className="space-y-4 sm:space-y-6">
              {/* Bundle Helper Text */}
              {bundleResult.eligibleItemsCount > 0 && (
                <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/30 px-4 py-3">
                  <p className="text-sm text-emerald-300 text-center font-medium">
                    {getBundleHelperText(bundleResult.eligibleItemsCount)}
                  </p>
                </div>
              )}

              {/* Cart Items */}
              <div className="space-y-3 sm:space-y-4">
                {items.map((item) => {
                  const isEligible = isBundleEligible(item.price, item.name);
                  return (
                  <div
                    key={item.id}
                    className="flex gap-3 sm:gap-4 rounded-xl sm:rounded-2xl border border-slate-700/40 bg-slate-900/50 p-4 sm:p-5 backdrop-blur-sm"
                  >
                    {/* Product Thumbnail */}
                    <div className="flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 rounded-lg overflow-hidden bg-slate-800/50 border border-slate-700/30">
                      {item.image ? (
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-full h-full object-contain p-1"
                        />
                      ) : (
                        <ProductImagePlaceholder className="w-full h-full" aspectRatio="" />
                      )}
                    </div>
                    
                    <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                      <div className="flex-1 min-w-0">
                        <Link 
                          to={item.slug}
                          className="font-semibold text-base sm:text-lg text-emerald-100 truncate block hover:text-emerald-200 transition-colors"
                        >
                          {item.name}
                        </Link>
                        <div className="flex items-center gap-2 mt-0.5 sm:mt-1">
                          <p className="text-sm text-slate-300">
                            £{item.price.toFixed(2)} each
                          </p>
                          {isEligible && (
                            <span className="text-[10px] text-emerald-400 bg-emerald-500/10 px-1.5 py-0.5 rounded">
                              Multi-buy
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center justify-between sm:justify-end gap-3 sm:gap-4">
                        {/* Quantity controls */}
                        <div className="flex items-center gap-2 sm:gap-3 rounded-full border border-slate-700/50 bg-slate-800/50 px-3 sm:px-4 py-1.5 sm:py-2">
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity - 1)}
                            className="text-slate-300 transition-colors hover:text-emerald-300 p-0.5"
                            aria-label="Decrease quantity"
                          >
                            <Minus className="h-3 w-3 sm:h-4 sm:w-4" />
                          </button>
                          <span className="min-w-[1.5rem] sm:min-w-[2rem] text-center text-sm sm:text-base text-slate-200 font-medium">
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                            className="text-slate-300 transition-colors hover:text-emerald-300 p-0.5"
                            aria-label="Increase quantity"
                          >
                            <Plus className="h-3 w-3 sm:h-4 sm:w-4" />
                          </button>
                        </div>

                        {/* Line total */}
                        <span className="min-w-[4rem] sm:min-w-[5rem] text-right font-semibold text-emerald-200 text-sm sm:text-base">
                          £{(item.price * item.quantity).toFixed(2)}
                        </span>

                        {/* Remove button */}
                        <button
                          onClick={() => removeItem(item.id)}
                          className="rounded-full p-1.5 sm:p-2 text-slate-400 transition-colors hover:bg-red-500/10 hover:text-red-400"
                          aria-label="Remove item"
                        >
                          <Trash2 className="h-4 w-4 sm:h-5 sm:w-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>

              {/* Cart Summary */}
              <div className="border-t border-slate-700/50 pt-4 sm:pt-6 mt-4 sm:mt-6">
                {/* Subtotal */}
                <div className="flex items-center justify-between mb-2">
                  <span className="text-base text-slate-300">Subtotal</span>
                  <span className="text-lg text-slate-100">
                    £{totalPrice.toFixed(2)}
                  </span>
                </div>
                
                {/* Bundle Discount */}
                {bundleResult.totalDiscount > 0 && bundleResult.bundleDetails && (
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Tag className="h-4 w-4 text-emerald-400" />
                      <span className="text-sm text-emerald-400">
                        Multi-buy discount
                        <span className="text-xs text-slate-400 ml-1">
                          ({bundleResult.bundleDetails.bundlesApplied.map(b => `${b.count}× ${b.type}`).join(', ')})
                        </span>
                      </span>
                    </div>
                    <span className="text-emerald-400 font-medium">
                      -£{bundleResult.totalDiscount.toFixed(2)}
                    </span>
                  </div>
                )}
                
                {/* Shipping */}
                <div className="rounded-xl bg-slate-800/30 border border-slate-700/30 p-3 sm:p-4 mb-4 sm:mb-6">
                  <div className="flex items-center justify-between text-sm mb-1">
                    <div className="flex items-center gap-2 text-slate-300">
                      <Truck className="w-4 h-4 text-emerald-400" />
                      <span>Shipping</span>
                    </div>
                    {shippingResult.isFreeShipping ? (
                      <span className="text-emerald-400 font-medium">FREE</span>
                    ) : (
                      <span className="text-slate-100">£{shippingResult.shippingCost.toFixed(2)}</span>
                    )}
                  </div>
                  <p className="text-xs text-slate-400 ml-6">
                    {shippingResult.message}
                  </p>
                </div>
                
                {/* Order Total */}
                <div className="flex items-center justify-between mb-4 pt-2 border-t border-slate-700/50">
                  <span className="text-lg font-medium text-slate-100">Order Total</span>
                  <span className="text-2xl font-bold text-emerald-300">
                    £{orderTotal.toFixed(2)}
                  </span>
                </div>

                <div className="flex flex-col gap-3">
                  <Button
                    onClick={handleCheckout}
                    size="lg"
                    className="w-full bg-gradient-to-r from-emerald-400 via-emerald-500 to-teal-500 text-slate-950 font-semibold shadow-[0_18px_55px_rgba(16,185,129,0.45)] hover:shadow-[0_0_55px_rgba(16,185,129,0.6)] py-5 sm:py-6 text-sm sm:text-base"
                  >
                    Proceed to checkout
                    <ArrowRight className="ml-2 h-4 w-4 sm:h-5 sm:w-5" />
                  </Button>
                  <Button
                    asChild
                    size="lg"
                    variant="outline"
                    className="w-full border-slate-600 text-slate-200 hover:bg-slate-800/50 text-sm sm:text-base"
                  >
                    <Link to="/shop">Continue shopping</Link>
                  </Button>
                </div>

                {/* Clear cart link */}
                <button
                  onClick={clearCart}
                  className="w-full mt-3 sm:mt-4 text-xs sm:text-sm text-slate-400 transition-colors hover:text-red-400"
                >
                  Clear cart
                </button>
                
                {/* Trust & Policy Links */}
                <div className="mt-6 pt-4 border-t border-slate-700/30">
                  <div className="flex flex-wrap items-center justify-center gap-3 text-xs text-slate-400 mb-3">
                    <span className="flex items-center gap-1">
                      <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
                      Secure Stripe checkout
                    </span>
                    <span className="flex items-center gap-1">
                      <Truck className="w-3.5 h-3.5 text-emerald-400" />
                      Free delivery over £{SHIPPING_THRESHOLD}
                    </span>
                    <span className="flex items-center gap-1">
                      <RotateCcw className="w-3.5 h-3.5 text-emerald-400" />
                      14-day returns
                    </span>
                  </div>
                  <div className="flex items-center justify-center gap-3 text-xs">
                    <Link to="/delivery" className="text-emerald-300 hover:text-emerald-200 underline">
                      Delivery info
                    </Link>
                    <span className="text-slate-600">•</span>
                    <Link to="/returns" className="text-emerald-300 hover:text-emerald-200 underline">
                      Returns policy
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
